import { CheckCircle2, AlertTriangle, XCircle, Minus, ShieldX, ShieldAlert, TriangleAlert, Info, CircleDot } from "lucide-react";

/* Shared chart/report primitives used by both the on-screen dashboard and the
   printable ReportDocument so the two never drift apart. */

export const SEVERITY_COLORS = {
  Critical: "#991b1b",
  High: "#ef4444",
  Medium: "#f59e0b",
  Low: "#64748b",
  Observation: "#94a3b8",
  Info: "#dc2626",
};

export const sevColor = (s) => SEVERITY_COLORS[s] || SEVERITY_COLORS.Observation;

export function RiskGauge({ score, label, size = 120, staticFrame = false }) {
  const safeScore = Math.max(0, Math.min(100, Number(score) || 0));
  const c = safeScore >= 80 ? "#22c55e" : safeScore >= 60 ? "#84cc16" : safeScore >= 40 ? "#eab308" : safeScore >= 20 ? "#dc2626" : "#ef4444";
  const r = size / 2 - 10;
  const circ = Math.PI * r;
  const prog = (safeScore / 100) * circ;
  return (
    <div className="flex flex-col items-center" style={{ position: "relative" }}>
      <svg width={size} height={size / 2 + 10} viewBox={`0 0 ${size} ${size / 2 + 10}`}>
        <path d={`M 10 ${size / 2 + 5} A ${r} ${r} 0 0 1 ${size - 10} ${size / 2 + 5}`} fill="none" stroke="#e5e7eb" strokeWidth="10" strokeLinecap="round" />
        <path d={`M 10 ${size / 2 + 5} A ${r} ${r} 0 0 1 ${size - 10} ${size / 2 + 5}`} fill="none" stroke={c} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${prog} ${circ}`} className={staticFrame ? "" : "transition-all duration-1000 ease-out"} />
      </svg>
      <div style={{ marginTop: "-8px", textAlign: "center" }}>
        <span style={{ fontSize: "28px", fontWeight: 900, color: c, fontFamily: "monospace", lineHeight: 1 }}>
          {safeScore}%
        </span>
      </div>
      <span className="text-xs text-muted-foreground mt-2 font-medium">{label}</span>
    </div>
  );
}

export function MiniSparkline({ values = [], color = "#dc2626" }) {
  if (!values.length) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(max - min, 1);
  const width = 84;
  const height = 24;
  const points = values.map((v, i) => {
    const x = (i / Math.max(values.length - 1, 1)) * width;
    const y = height - ((v - min) / span) * (height - 2) - 1;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="mt-1 opacity-90">
      <polyline fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={points} />
    </svg>
  );
}

export function HeatmapCell({ value, max, label }) {
  const i = max > 0 ? value / max : 0;
  const bg = i > 0.75 ? "bg-red-500" : i > 0.5 ? "bg-red-400" : i > 0.25 ? "bg-yellow-400" : i > 0 ? "bg-green-400" : "bg-slate-100 dark:bg-slate-800";
  const tx = i > 0.5 ? "text-white" : "text-slate-800 dark:text-slate-200";
  return (
    <div className={`${bg} ${tx} rounded p-2 text-center text-xs font-mono font-bold transition-all hover:scale-105`} title={`${label}: ${value}`}>
      {value}
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    Compliant: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30",
    "Partially Compliant": "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30",
    "Non-Compliant": "bg-red-100 text-red-800 border-red-300 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30",
    "Not Assessed": "bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/30",
  };
  const icons = {
    Compliant: <CheckCircle2 className="w-3 h-3" />,
    "Partially Compliant": <AlertTriangle className="w-3 h-3" />,
    "Non-Compliant": <XCircle className="w-3 h-3" />,
    "Not Assessed": <Minus className="w-3 h-3" />,
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${map[status] || map["Not Assessed"]}`}>
      {icons[status] || icons["Not Assessed"]}
      {status}
    </span>
  );
}

export function SeverityIcon({ severity }) {
  const s = severity?.toLowerCase();
  if (s === "critical") return <ShieldX className="w-4 h-4 text-red-800 dark:text-red-300" />;
  if (s === "high") return <ShieldAlert className="w-4 h-4 text-red-500 dark:text-red-300" />;
  if (s === "medium") return <TriangleAlert className="w-4 h-4 text-amber-500 dark:text-amber-300" />;
  if (s === "low") return <Info className="w-4 h-4 text-slate-500 dark:text-slate-300" />;
  return <CircleDot className="w-4 h-4 text-slate-400 dark:text-slate-500" />;
}

/* severity → inline border color used inside the printable document */
const sevBorderColorMap = { Critical: "#991b1b", High: "#ef4444", Medium: "#f59e0b", Low: "#64748b", Observation: "#94a3b8" };
const sevBgColorMap = { Critical: "#fef2f2", High: "#fef2f2", Medium: "#fffbeb", Low: "#f8fafc", Observation: "#f8fafc" };

export const sevBorderColor = (s) => sevBorderColorMap[s] || "#cbd5e1";
export const sevBgColor = (s) => sevBgColorMap[s] || "#f9fafb";